<!--
  @component Navbar.MegaMenuHeader

  A component for rendering the header of a mega menu in the navbar.
  It handles focus, blur, and keyboard navigation events.

  @prop title - The title of the mega menu header
  @prop href - The URL the header links to (optional)
  @prop css - Custom CSS styles to apply to the header

  Example:
  <Navbar.MegaMenuHeader title="Electronics" href="/electronics">
    <CustomContent />
  </Navbar.MegaMenuHeader>
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { megaMenuStyles } from '../navbar.styles';
  import { getContext } from 'svelte';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarItemContext, NavbarMegaMenuHeaderProps } from '../navbar.types';
  import { goto } from '$app/navigation';

  /** Props for the Navbar.MegaMenuHeader component */
  const { children, css: cssProp, titleStyles, title, href }: NavbarMegaMenuHeaderProps = $props();

  const styles = megaMenuStyles.raw();
  /** Navbar context for managing state */
  const navbarContext = getContext<NavbarStore>('navbar');
  /** Navbar item context for accessing parent item properties */
  const navbarItemContext = getContext<NavbarItemContext>('navbar-item');

  /**
   * Handles focus event on the header
   * Clears any existing close timeout and sets a new hover timeout
   */
  const onfocus = () => {
    if (navbarContext.closeTimeoutId) {
      clearTimeout(navbarContext.closeTimeoutId);
      navbarContext.setCloseTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(navbarItemContext.id);
    }, navbarItemContext.hoverDelay);

    navbarContext.setHoverTimeout(timeout as unknown as number);
  }

  /**
   * Handles blur event on the header
   * Clears any existing hover timeout and sets a new close timeout
   */
  const onblur = () => {
    if (navbarContext.hoverTimeoutId) {
      clearTimeout(navbarContext.hoverTimeoutId);
      navbarContext.setHoverTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(null);
    }, navbarItemContext.closeDelay);

    navbarContext.setCloseTimeout(timeout as unknown as number);
  }

  /**
   * Handles keydown events for keyboard navigation
   * @param {KeyboardEvent} event - The keydown event
   */
  const onkeydown = (event: KeyboardEvent) => {
    const eventTarget = event.target as HTMLElement;
    switch (event.key) {
      case 'Enter':
        handleEnter();
        break;
      case 'Escape':
        handleEscape();
        break;
      case 'ArrowDown':
        handleArrowDown(eventTarget);
        break;
      case 'ArrowUp':
        handleArrowUp(eventTarget);
        break;
    }
  }

  const handleEnter = () => {
    if (href) {
      goto(href);
    }
    navbarContext.closeMenu();
  }

  const handleEscape = () => {
    navbarContext.closeMenu();
    // Focuses the current nav item
    const item = document.querySelector(`[data-id="${navbarItemContext.id}"]`) as HTMLElement;
    item?.focus();
  }

  const handleArrowDown = (eventTarget: HTMLElement) => {
    let downTarget: HTMLElement | undefined;

    if (eventTarget?.parentElement?.nextElementSibling) {
      downTarget = eventTarget.parentElement.nextElementSibling as HTMLElement;
    } else if (eventTarget?.parentElement?.parentElement?.nextElementSibling?.children[0].children[0]) {
      downTarget = eventTarget.parentElement.parentElement.nextElementSibling.children[0].children[0] as HTMLElement;
    } else {
      downTarget = document.querySelector(`[data-id="${navbarItemContext.id}"]`)?.parentElement?.nextElementSibling?.children[0] as HTMLElement;
    }

    downTarget?.focus();
  }

  const handleArrowUp = (eventTarget: HTMLElement) => {
    let upTarget: HTMLElement | undefined;
    const children = eventTarget?.parentElement?.parentElement?.previousElementSibling?.children;

    if (children) {
      if (children.length > 1) {
        upTarget = children[children.length - 1] as HTMLElement;
      } else {
        upTarget = children[0].children[0] as HTMLElement;
      }
    } else {
      upTarget = document?.querySelector(`[data-id="${navbarItemContext.id}"]`) as HTMLElement;
    }

    upTarget?.focus();
  }
</script>

<div role="menuitem" class={css(styles.header, cssProp)}>
  {#if title}
    {#if href}
      <a
        {onkeydown}
        {onfocus}
        {onblur}
        href={href}
        onclick={() => navbarContext.closeMenu()}
      >
        <h2 class={css(styles.title, titleStyles)}>{title}</h2>
      </a>
    {:else}
      <span
        {onfocus}
        {onblur}
      >
        <h2>{title}</h2>
      </span>
    {/if}
  {/if}
  {#if children}
    {@render children()}
  {/if}
</div>
